import { useEffect, useState, useCallback } from "react";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Download, Send, Loader2, Trash2, Search, Receipt } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import InvoiceDetailDialog, { STATUS_TONE, money } from "@/components/InvoiceDetailDialog";

// Stable local id so React keys survive line reordering/removal
let _lineSeq = 0;
const emptyLine = () => ({ _lid: `l-${++_lineSeq}`, description: "", hsn_sac: "998231", quantity: 1, unit_price: 0, gst_rate: 18 });

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [newOpen, setNewOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (statusF !== "all") params.status = statusF;
    api.get("/invoices", { params }).then(({ data }) => setInvoices(data));
  }, [q, statusF]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data)); }, []);

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  const [form, setForm] = useState({ client_id: "", invoice_date: new Date().toISOString().slice(0, 10), due_date: "", notes: "", lines: [emptyLine()] });

  const preview = () => {
    let sub = 0, gst = 0;
    for (const l of form.lines) {
      const t = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
      sub += t;
      gst += t * (Number(l.gst_rate) || 0) / 100;
    }
    return { sub, gst, total: sub + gst };
  };

  const create = async () => {
    if (!form.client_id) return toast.error("Select a client");
    if (form.lines.some((l) => !l.description || Number(l.unit_price) <= 0))
      return toast.error("Every line needs a description and non-zero unit price");
    setBusy(true);
    try {
      const { data } = await api.post("/invoices", form);
      toast.success(`Invoice ${data.invoice_number} created`);
      setNewOpen(false);
      setForm({ client_id: "", invoice_date: new Date().toISOString().slice(0, 10), due_date: "", notes: "", lines: [emptyLine()] });
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to create invoice"));
    } finally {
      setBusy(false);
    }
  };

  const preview_ = preview();

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-56">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search #, notes…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" data-testid="invoices-search" />
        </div>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="h-9 w-40" data-testid="invoices-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {["draft","issued","partially_paid","paid","overdue","cancelled"].map(s =>
              <SelectItem key={s} value={s} className="capitalize">{s.replace("_"," ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <div className="text-xs text-muted-foreground font-mono">{invoices.length} invoices</div>
        <Button size="sm" onClick={() => setNewOpen(true)} data-testid="new-invoice-btn"><Plus size={13} className="mr-1.5" /> New invoice</Button>
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr className="text-left">
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Invoice #</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Client</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Date</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Due</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground text-right">Amount</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground text-right">Outstanding</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-12 text-center text-sm text-muted-foreground">
                <Receipt className="inline mr-2 mb-1 opacity-40" size={16} />
                No invoices yet. <button onClick={() => setNewOpen(true)} className="underline">Create your first invoice</button>.
              </td></tr>
            )}
            {invoices.map((inv) => (
              <tr key={inv.id} onClick={() => setDetail(inv)} className="border-b border-border hover:bg-muted/40 cursor-pointer" data-testid={`invoice-row-${inv.id}`}>
                <td className="px-3 py-2.5 font-mono text-xs font-medium">{inv.invoice_number}</td>
                <td className="px-3 py-2.5">{clientName(inv.client_id)}</td>
                <td className="px-3 py-2.5 text-xs font-mono text-muted-foreground">{formatDate(inv.invoice_date, "dd MMM yyyy")}</td>
                <td className="px-3 py-2.5 text-xs font-mono text-muted-foreground">{inv.due_date ? formatDate(inv.due_date, "dd MMM yyyy") : "—"}</td>
                <td className="px-3 py-2.5 text-right font-mono">{money(inv.grand_total)}</td>
                <td className="px-3 py-2.5 text-right font-mono">{money(inv.amount_due)}</td>
                <td className="px-3 py-2.5"><Badge variant="outline" className={cn("text-[10px] capitalize", STATUS_TONE[inv.invoice_status])}>{inv.invoice_status.replace("_"," ")}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* New invoice dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="new-invoice-dialog">
          <DialogHeader>
            <DialogTitle>New invoice</DialogTitle>
            <DialogDescription>Create a draft invoice. It becomes billable when you issue it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5 col-span-1">
                <Label className="text-xs">Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="inv-client"><SelectValue placeholder="Select client…" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Invoice date</Label>
                <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="inv-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Due date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="inv-due" />
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Line items</div>
              <div className="border border-border rounded-md">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground bg-muted/50">
                    <tr><th className="px-2 py-1.5 text-left">Description</th><th className="px-2 py-1.5">HSN/SAC</th><th className="px-2 py-1.5">Qty</th><th className="px-2 py-1.5">Rate</th><th className="px-2 py-1.5">GST %</th><th></th></tr>
                  </thead>
                  <tbody>
                    {form.lines.map((ln, i) => (
                      <tr key={ln._lid} className="border-t border-border" data-testid={`inv-line-${i}`}>
                        <td className="p-1"><Input value={ln.description} onChange={(e) => { const l=[...form.lines];l[i].description=e.target.value;setForm({...form,lines:l}); }} className="h-8" /></td>
                        <td className="p-1 w-24"><Input value={ln.hsn_sac} onChange={(e) => { const l=[...form.lines];l[i].hsn_sac=e.target.value;setForm({...form,lines:l}); }} className="h-8 font-mono text-xs" /></td>
                        <td className="p-1 w-16"><Input type="number" min={0} step="0.01" value={ln.quantity} onChange={(e) => { const l=[...form.lines];l[i].quantity=Number(e.target.value);setForm({...form,lines:l}); }} className="h-8" /></td>
                        <td className="p-1 w-24"><Input type="number" min={0} step="0.01" value={ln.unit_price} onChange={(e) => { const l=[...form.lines];l[i].unit_price=Number(e.target.value);setForm({...form,lines:l}); }} className="h-8" /></td>
                        <td className="p-1 w-20"><Input type="number" min={0} max={28} value={ln.gst_rate} onChange={(e) => { const l=[...form.lines];l[i].gst_rate=Number(e.target.value);setForm({...form,lines:l}); }} className="h-8" /></td>
                        <td className="p-1 w-10">
                          {form.lines.length > 1 && (
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => setForm({ ...form, lines: form.lines.filter((_, x) => x !== i) })}>
                              <Trash2 size={13} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, lines: [...form.lines, emptyLine()] })} className="mt-1" data-testid="inv-add-line">
                <Plus size={12} className="mr-1" /> Add line
              </Button>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional note visible on the PDF" />
            </div>

            <div className="flex items-center justify-end gap-6 border-t border-border pt-3 text-sm">
              <div className="text-muted-foreground">Subtotal <span className="font-mono ml-1">{money(preview_.sub)}</span></div>
              <div className="text-muted-foreground">GST <span className="font-mono ml-1">{money(preview_.gst)}</span></div>
              <div className="font-heading font-semibold">Total <span className="font-mono ml-1">{money(preview_.total)}</span></div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={busy || !form.client_id} data-testid="inv-submit">
              {busy && <Loader2 size={12} className="animate-spin mr-1.5" />} Create draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {detail && (
        <InvoiceDetailDialog
          invoiceId={detail.id}
          clientName={clientName(detail.client_id)}
          onClose={() => setDetail(null)}
          onChange={load}
        />
      )}
    </div>
  );
}
